import { supabase } from "@/platform/lib/supabase";
import { fetchQuestionPageImages } from "@/tenants/usp/lib/page-image";

export type UspQuestionStatus = "draft" | "review" | "approved" | "archived";
export type UspQuestionKind = "objective" | "discursive" | "essay";

export interface UspAdminQuestion {
  id: string;
  year: number;
  phase: string;
  day: number | null;
  number: number | null;
  kind: UspQuestionKind;
  discipline: string;
  topic: string | null;
  subtopic: string | null;
  cognitive_skill: string | null;
  question_type: string | null;
  pattern: string | null;
  difficulty: string | null;
  stem: string;
  alternatives: Record<string, string> | { items?: unknown } | null;
  correct_answer: string | null;
  expected_answer: string | null;
  has_image: boolean;
  has_graph: boolean;
  has_table: boolean;
  status: UspQuestionStatus;
  llm_confidence: number | null;
  human_reviewed_at: string | null;
  exam_paper_id: string | null;
  page_number: number | null;
  pageImageUrl?: string;
}

export interface UspQaFilters {
  status?: UspQuestionStatus | "all";
  discipline?: string | "all";
  kind?: UspQuestionKind | "all";
  year?: number | "all";
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface UspQaPage {
  rows: UspAdminQuestion[];
  total: number;
  page: number;
  pageSize: number;
}

export async function listUspQuestions(f: UspQaFilters = {}): Promise<UspQaPage> {
  const page = Math.max(1, f.page ?? 1);
  const pageSize = Math.min(100, Math.max(5, f.pageSize ?? 20));
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let q = supabase
    .from("usp_questions")
    .select(
      "id, year, phase, day, number, kind, discipline, topic, subtopic, cognitive_skill, question_type, pattern, difficulty, stem, alternatives, correct_answer, expected_answer, has_image, has_graph, has_table, status, llm_confidence, human_reviewed_at, exam_paper_id, page_number",
      { count: "exact" },
    )
    .order("status", { ascending: true })
    .order("year", { ascending: false })
    .order("discipline")
    .order("number", { ascending: true });

  if (f.status && f.status !== "all") q = q.eq("status", f.status);
  if (f.discipline && f.discipline !== "all") q = q.eq("discipline", f.discipline);
  if (f.kind && f.kind !== "all") q = q.eq("kind", f.kind);
  if (f.year && f.year !== "all") q = q.eq("year", f.year);
  if (f.search && f.search.trim().length > 0) q = q.ilike("stem", `%${f.search.trim()}%`);

  const { data, count, error } = await q.range(from, to);
  if (error) throw error;

  const rows = (data ?? []) as UspAdminQuestion[];
  const images = await fetchQuestionPageImages(rows);
  for (const r of rows) if (images[r.id]) r.pageImageUrl = images[r.id];

  return { rows, total: count ?? rows.length, page, pageSize };
}

export async function setUspQuestionStatus(
  id: string,
  status: UspQuestionStatus,
  reviewerId?: string,
): Promise<void> {
  const { error } = await supabase
    .from("usp_questions")
    .update({
      status,
      human_reviewed_by: reviewerId ?? null,
      human_reviewed_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;
}

export async function patchUspQuestion(
  id: string,
  patch: Partial<{
    stem: string;
    correct_answer: string | null;
    discipline: string;
    topic: string | null;
    difficulty: string | null;
    expected_answer: string | null;
  }>,
): Promise<void> {
  const { error } = await supabase
    .from("usp_questions")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export interface UspCorpusStats {
  byStatus: Record<string, number>;
  byDiscipline: Array<{ discipline: string; total: number; approved: number }>;
  total: number;
}

export async function getUspCorpusStats(): Promise<UspCorpusStats> {
  const { data } = await supabase.from("usp_questions").select("status, discipline");
  const rows = (data ?? []) as Array<{ status: string; discipline: string }>;
  const byStatus: Record<string, number> = {};
  const byDiscMap: Record<string, { total: number; approved: number }> = {};
  for (const r of rows) {
    byStatus[r.status] = (byStatus[r.status] ?? 0) + 1;
    const b = byDiscMap[r.discipline] ?? { total: 0, approved: 0 };
    b.total++;
    if (r.status === "approved") b.approved++;
    byDiscMap[r.discipline] = b;
  }
  return {
    byStatus,
    total: rows.length,
    byDiscipline: Object.entries(byDiscMap)
      .map(([discipline, v]) => ({ discipline, ...v }))
      .sort((a, b) => b.total - a.total),
  };
}
